import { useState, useEffect, useCallback } from "react";

const FOLLOWED_KEY = "se_followed_podcasts";
const NOTIFICATIONS_KEY = "se_notifications";

export interface FollowedPodcast {
  id: string;
  name: string;
  logoUrl?: string;
  lastKnownEpisode?: string;
  followedAt: number;
}

export type NotificationType =
  | "follow"
  | "new_episode"
  | "welcome"
  | "milestone";

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  podcastId?: string;
  podcastName?: string;
  logoUrl?: string;
  timestamp: number;
  read: boolean;
}

// Re-export for backwards compatibility
export type NewEpisodeNotification = Notification;

export function useFollowedPodcasts() {
  const [followed, setFollowed] = useState<FollowedPodcast[]>(() => {
    try {
      const saved = localStorage.getItem(FOLLOWED_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [notifications, setNotifications] = useState<Notification[]>(() => {
    try {
      const saved = localStorage.getItem(NOTIFICATIONS_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Persist followed podcasts
  useEffect(() => {
    try {
      localStorage.setItem(FOLLOWED_KEY, JSON.stringify(followed));
    } catch (e) {
      console.error("Failed to save followed podcasts:", e);
    }
  }, [followed]);

  // Persist notifications
  useEffect(() => {
    try {
      localStorage.setItem(NOTIFICATIONS_KEY, JSON.stringify(notifications));
    } catch (e) {
      console.error("Failed to save notifications:", e);
    }
  }, [notifications]);

  // Calculate unread count
  const unreadCount = notifications.filter((n) => !n.read).length;

  // Add a new notification
  const addNotification = useCallback(
    (notification: Omit<Notification, "id" | "timestamp" | "read">) => {
      const newNotif: Notification = {
        ...notification,
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        timestamp: Date.now(),
        read: false,
      };

      setNotifications((prev) => [newNotif, ...prev].slice(0, 50)); // Keep max 50
    },
    [],
  );

  // Follow a podcast (and create notification)
  const followPodcast = useCallback(
    (podcast: {
      id: string;
      name: string;
      logoUrl?: string;
      lastEpisode?: string;
    }) => {
      setFollowed((prev) => {
        // Don't add duplicates
        if (prev.some((p) => p.id === podcast.id)) return prev;

        return [
          ...prev,
          {
            id: podcast.id,
            name: podcast.name,
            logoUrl: podcast.logoUrl,
            lastKnownEpisode: podcast.lastEpisode,
            followedAt: Date.now(),
          },
        ];
      });

      // Create follow notification
      addNotification({
        type: "follow",
        title: "Nu följer du",
        message: podcast.name,
        podcastId: podcast.id,
        podcastName: podcast.name,
        logoUrl: podcast.logoUrl,
      });
    },
    [addNotification],
  );

  // Unfollow a podcast
  const unfollowPodcast = useCallback((podcastId: string) => {
    setFollowed((prev) => prev.filter((p) => p.id !== podcastId));
  }, []);

  // Check if a podcast is followed
  const isFollowed = useCallback(
    (podcastId: string) => {
      return followed.some((p) => p.id === podcastId);
    },
    [followed],
  );

  // Toggle follow status
  const toggleFollow = useCallback(
    (podcast: {
      id: string;
      name: string;
      logoUrl?: string;
      lastEpisode?: string;
    }) => {
      if (isFollowed(podcast.id)) {
        unfollowPodcast(podcast.id);
      } else {
        followPodcast(podcast);
      }
    },
    [isFollowed, followPodcast, unfollowPodcast],
  );

  // Mark all as read
  const markAllRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  // Clear all notifications
  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // Remove single notification
  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  return {
    followed,
    followPodcast,
    unfollowPodcast,
    isFollowed,
    toggleFollow,
    notifications,
    unreadCount,
    addNotification,
    markAllRead,
    clearNotifications,
    removeNotification,
  };
}
